import { useMemo, useState } from "react";
import { Trophy, Play, X as XIcon, Sparkles, ExternalLink } from "lucide-react";

function toEmbed(url) {
  if (!url) return null;
  try {
    const u = new URL(url);
    const host = u.hostname.replace(/^www\./, "");
    // YouTube
    if (host === "youtu.be") {
      const id = u.pathname.slice(1);
      return id ? `https://www.youtube.com/embed/${id}?autoplay=1&rel=0` : null;
    }
    if (host.endsWith("youtube.com")) {
      const id = u.searchParams.get("v");
      if (id) return `https://www.youtube.com/embed/${id}?autoplay=1&rel=0`;
      // /embed/xxxx or /shorts/xxxx
      const parts = u.pathname.split("/").filter(Boolean);
      if (parts.length >= 2 && (parts[0] === "embed" || parts[0] === "shorts")) {
        return `https://www.youtube.com/embed/${parts[1]}?autoplay=1&rel=0`;
      }
    }
    // Twitch
    if (host.endsWith("twitch.tv")) {
      const parent = typeof window !== "undefined" ? window.location.hostname : "localhost";
      const parts = u.pathname.split("/").filter(Boolean);
      if (parts[0] === "videos" && parts[1]) {
        return `https://player.twitch.tv/?video=${parts[1]}&parent=${parent}&autoplay=true`;
      }
      if (parts[0] && parts[0] !== "directory") {
        return `https://player.twitch.tv/?channel=${parts[0]}&parent=${parent}&autoplay=true`;
      }
    }
  } catch {
    return null;
  }
  return null;
}

export default function ChampionTrailer({ team, highlightUrl, ringNote, mvp, season, year }) {
  const [open, setOpen] = useState(false);
  const embed = useMemo(() => toEmbed(highlightUrl), [highlightUrl]);

  return (
    <section
      data-testid="champion-trailer"
      className="border border-[#F4D03F]/60 bg-gradient-to-br from-[#F4D03F]/10 via-transparent to-[#FF007F]/10 p-5 sm:p-8"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-[10px] font-mono tracking-[0.3em] text-[#F4D03F] uppercase">
            <Trophy className="w-4 h-4" />
            Champion Trailer {season ? `· ${season}` : ""}{year ? ` · ${year}` : ""}
          </div>
          <h2
            className="font-heading text-3xl sm:text-4xl font-black uppercase tracking-tighter mt-2"
            style={{ color: team?.logo_color }}
          >
            {team?.name || "Champion"}
          </h2>
          {ringNote && (
            <p className="text-sm text-gray-300 mt-2 max-w-2xl leading-relaxed">{ringNote}</p>
          )}
          {mvp && (
            <div className="mt-3 inline-flex items-center gap-2 border border-[#F4D03F]/40 px-3 py-1.5">
              <Sparkles className="w-3.5 h-3.5 text-[#F4D03F]" />
              <span className="text-[10px] font-mono uppercase tracking-widest text-gray-500">Final MVP</span>
              <span className="font-heading font-bold uppercase tracking-tight">{mvp.handle}</span>
            </div>
          )}
        </div>
        <div className="flex gap-2 flex-wrap">
          {embed && (
            <button
              onClick={() => setOpen(true)}
              data-testid="champion-trailer-play"
              className="rounded-none bg-[#F4D03F] hover:bg-yellow-300 text-black px-5 py-2.5 uppercase tracking-widest text-xs font-bold flex items-center gap-2"
            >
              <Play className="w-4 h-4" /> Watch trailer
            </button>
          )}
          {highlightUrl && !embed && (
            <a
              href={highlightUrl}
              target="_blank"
              rel="noreferrer"
              data-testid="champion-trailer-external"
              className="rounded-none border border-[#F4D03F] text-[#F4D03F] hover:bg-[#F4D03F] hover:text-black px-4 py-2 uppercase tracking-widest text-xs font-bold flex items-center gap-2"
            >
              <ExternalLink className="w-3.5 h-3.5" /> Open highlight
            </a>
          )}
        </div>
      </div>

      {open && embed && (
        <div
          data-testid="champion-trailer-modal"
          className="fixed inset-0 z-50 bg-black/90 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setOpen(false)}
        >
          <div className="relative w-full max-w-5xl" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setOpen(false)}
              data-testid="champion-trailer-close"
              className="absolute -top-12 right-0 text-white hover:text-[#F4D03F] flex items-center gap-1 text-xs font-mono uppercase tracking-widest"
            >
              <XIcon className="w-4 h-4" /> Close
            </button>
            <div className="aspect-video border border-[#F4D03F]/60 bg-black overflow-hidden">
              <iframe
                data-testid="champion-trailer-iframe"
                src={embed}
                title="Champion trailer"
                className="w-full h-full"
                allow="autoplay; encrypted-media; picture-in-picture"
                allowFullScreen
              />
            </div>
          </div>
        </div>
      )}

      {!highlightUrl && (
        <div className="mt-4 text-[10px] font-mono uppercase tracking-widest text-gray-500">
          Highlight reel drops soon — admins can paste a YouTube or Twitch clip URL on the champion record.
        </div>
      )}
    </section>
  );
}
